import { CacheProvider } from '@emotion/react';
import { useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, createMemoryRouter, type RouteObject } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { BankCashPage } from '../features/finance/BankCashPage';
import { MovementDetailPage } from '../features/finance/BankMovements';
import { CreditNoteDetailPage } from '../features/finance/CreditNoteDetailPage';
import { CreditNotesPage, NewCreditNotePage } from '../features/finance/CreditNotesPage';
import { PaymentDetailPage } from '../features/finance/PaymentDetailPage';
import { PaymentsPage } from '../features/finance/PaymentsPage';
import { SupplierInvoiceDetailPage } from '../features/finance/SupplierInvoiceDetailPage';
import { SupplierInvoicesPage } from '../features/finance/SupplierInvoicesPage';
import { AccountingPeriodsPage, PeriodDetailPage } from '../features/ledger/AccountingPeriodsPage';
import { JournalEditorPage } from '../features/ledger/JournalEditorPage';
import { ExpenseCategoriesPage } from '../features/expenses/ExpenseCategoriesPage';
import {
  ExpenseClaimDetailPage,
  ExpenseClaimEditorPage,
  ExpenseClaimsPage,
} from '../features/expenses/ExpenseClaimsPages';
import { AnalysisReportPage } from '../features/reports/AnalysisReportPage';
import { FinancialReportsPage } from '../features/reports/FinancialReportsPage';
import {
  TaxableSuppliesPage,
  VatExceptionsPage,
  VatReconciliationPage,
  VatTransactionsPage,
} from '../features/reports/VatReportsPages';
import {
  CustomerOutstandingPage,
  OpeningBalanceReconciliationPage,
  ReconciliationStatusPage,
  SupplierOutstandingPage,
} from '../features/reports/SubledgerReportsPages';
import {
  CustomerCreditNoteDetailPage,
  CustomerCreditNotesPage,
  NewCustomerCreditNotePage,
} from '../features/receivables/CustomerCreditNotesPages';
import {
  CustomerInvoiceDetailPage,
  CustomerInvoiceEditorPage,
  CustomerInvoicesPage,
  TaxInvoicePrintPage,
} from '../features/receivables/CustomerInvoicesPages';
import {
  CustomerReceiptDetailPage,
  CustomerReceiptsPage,
  NewCustomerReceiptPage,
} from '../features/receivables/CustomerReceiptsPages';
import {
  AccountLedgerPage,
  BalanceSheetPage,
  PostingRejectionsPage,
  ProfitAndLossPage,
  TrialBalancePage,
} from '../features/reports/LedgerReportsPages';
import { AccountDetailPage } from '../features/ledger/AccountDetailPage';
import { ChartOfAccountsPage } from '../features/ledger/ChartOfAccountsPage';
import { CoaImportPage } from '../features/ledger/CoaImportPage';
import { JournalDetailPage, JournalsPage } from '../features/ledger/JournalsPage';
import { OrganizationPage } from '../features/organization/OrganizationPage';
import { OrganizationChartPage } from '../features/organization/OrganizationChartPage';
import {
  COST_CENTRES,
  DEPARTMENTS,
  LOCATIONS,
  MastersPage,
} from '../features/organization/MastersPage';
import { RolesPage } from '../features/admin/RolesPage';
import { UsersPage } from '../features/admin/UsersPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { PostingMappingsPage } from '../features/ledger/PostingMappingsPage';
import { TaxConfigurationPage } from '../features/tax/TaxConfigurationPage';
import { SupplierDetailPage } from '../features/procurement/SupplierDetailPage';
import { SuppliersPage } from '../features/procurement/SuppliersPage';
import { EmployeesPage } from '../features/hr/EmployeesPage';
import { PositionsPage } from '../features/hr/PositionsPage';
import { EmployeeDetailPage, MyEmployeeRecordPage } from '../features/hr/EmployeeDetailPage';
import { PositionDetailPage } from '../features/hr/PositionDetailPage';
import { RoleDetailPage } from '../features/admin/RoleDetailPage';
import { UserDetailPage } from '../features/admin/UserDetailPage';
import { AuditTrailPage } from '../features/admin/AuditTrailPage';
import { ConfigurationPage } from '../features/admin/ConfigurationPage';
import { SensitiveFieldsPage, SodRulesPage } from '../features/admin/ControlsPages';
import { ListValuesPage } from '../features/admin/ListValuesPage';
import { NumberSequencesPage } from '../features/admin/NumberSequencesPage';
import { WorkflowRoutesPage } from '../features/admin/WorkflowRoutesPage';
import { ApprovalChartPage } from '../features/admin/ApprovalChartPage';
import { ApprovalsPage } from '../features/approvals/ApprovalsPage';
import { NotificationRulesPage } from '../features/admin/NotificationRulesPage';
import { ChatPage } from '../features/chat/ChatPage';
import { ChatGroupDetailPage, ChatGroupsPage } from '../features/chat/ChatGroupsPage';
import { ApiDescriptionPage } from '../features/integration/ApiDescriptionPage';
import { NotificationsPage } from '../features/notifications/NotificationsPage';
import { DocumentTypesPage } from '../features/admin/DocumentTypesPage';
import { ExpiringDocumentsPage } from '../features/documents/ExpiringDocumentsPage';
import { AssetClassesPage } from '../features/assets/AssetClassesPage';
import { AssetDetailPage } from '../features/assets/AssetDetailPage';
import { AssetsPage } from '../features/assets/AssetsPage';
import { MyAssetsPage } from '../features/assets/MyAssetsPage';
import { ActivitiesPage, ActivityDetailPage } from '../features/crm/ActivitiesPage';
import { ClientDetailPage } from '../features/crm/ClientDetailPage';
import { ClientsPage } from '../features/crm/ClientsPage';
import { ProductsServicesPage } from '../features/organization/ProductsServicesPage';
import { ProductServiceDetailPage } from '../features/organization/ProductServiceDetailPage';
import { MasterDetailPage } from '../features/organization/MasterDetailPage';
import { ContactDetailPage } from '../features/crm/ContactDetailPage';
import { ProjectDetailPage } from '../features/projects/ProjectDetailPage';
import { ProjectsPage } from '../features/projects/ProjectsPage';
import { MyTasksPage, WorkProjectsPage } from '../features/projects/work/MyTasksPage';
import { ProjectWorkPage } from '../features/projects/work/ProjectWorkPage';
import { LeaveCancellationDetailPage } from '../features/hr/LeaveCancellation';
import { LeaveRequestDetailPage, LeaveRequestsPage, MyLeavePage } from '../features/hr/LeavePages';
import { LeaveTypesPage } from '../features/hr/LeaveTypesPage';
import { PublicHolidaysPage } from '../features/hr/PublicHolidaysPage';
import { AnnouncementDetailPage, AnnouncementsPage } from '../features/hr/AnnouncementsPage';
import {
  MyOnboardingTasksPage,
  OnboardingPage,
  OnboardingTemplatesPage,
} from '../features/hr/onboarding';
import {
  EmployeeRequestDetailPage,
  EmployeeRequestsPage,
  MyRequestsPage,
} from '../features/hr/EmployeeRequestPages';
import { SearchPage } from '../features/search/SearchPage';
import { CampaignDetailPage } from '../features/marketing/CampaignDetailPage';
import { CampaignsPage } from '../features/marketing/CampaignsPage';
import { LeadDetailPage } from '../features/marketing/LeadDetailPage';
import { LeadsPage } from '../features/marketing/LeadsPage';
import { HappinessPage } from '../features/happiness/HappinessPage';
import { QualityIssueDetailPage } from '../features/quality/QualityIssueDetailPage';
import { QualityIssuesPage } from '../features/quality/QualityIssuesPage';
import { ReportingDashboardPage } from '../features/reporting/DashboardPage';
import {
  GoodsReceiptDetailPage,
  GoodsReceiptsPage,
} from '../features/procurement/GoodsReceiptsPage';
import { PurchaseOrderDetailPage } from '../features/procurement/PurchaseOrderDetailPage';
import {
  EditPurchaseOrderPage,
  NewPurchaseOrderPage,
} from '../features/procurement/PurchaseOrderEditorPage';
import { PurchaseOrdersPage } from '../features/procurement/PurchaseOrdersPage';
import { PurchaseRequestDetailPage } from '../features/procurement/PurchaseRequestDetailPage';
import {
  EditPurchaseRequestPage,
  NewPurchaseRequestPage,
} from '../features/procurement/PurchaseRequestEditorPage';
import {
  MyPurchaseRequestsPage,
  PurchaseRequestsPage,
} from '../features/procurement/PurchaseRequestsPage';
import { BatchDetailPage } from '../features/migration/BatchDetailPage';
import { BatchesPage } from '../features/migration/BatchesPage';
import { AppShell } from './AppShell';
import { NotFoundPage } from './NotFoundPage';
import { appEmotionCache } from './emotion-cache';
import { theme } from './theme';

/**
 * Every route sits inside the shell, which resolves the session first: unauthenticated callers see
 * the sign-in screen instead of any page. The API enforces authorization regardless (SDD §22.4).
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'organization', element: <OrganizationPage /> },
      { path: 'organization/chart', element: <OrganizationChartPage /> },
      { path: 'organization/departments', element: <MastersPage kind={DEPARTMENTS} /> },
      { path: 'organization/cost-centres', element: <MastersPage kind={COST_CENTRES} /> },
      { path: 'organization/locations', element: <MastersPage kind={LOCATIONS} /> },
      {
        path: 'organization/departments/:id',
        element: <MasterDetailPage kind={DEPARTMENTS} />,
      },
      {
        path: 'organization/cost-centres/:id',
        element: <MasterDetailPage kind={COST_CENTRES} />,
      },
      { path: 'organization/locations/:id', element: <MasterDetailPage kind={LOCATIONS} /> },
      { path: 'hr/employees', element: <EmployeesPage /> },
      { path: 'hr/employees/:id', element: <EmployeeDetailPage /> },
      { path: 'hr/my-record', element: <MyEmployeeRecordPage /> },
      { path: 'hr/positions', element: <PositionsPage /> },
      { path: 'hr/positions/:id', element: <PositionDetailPage /> },
      { path: 'posting-mappings', element: <PostingMappingsPage /> },
      { path: 'tax', element: <TaxConfigurationPage /> },
      { path: 'admin/users', element: <UsersPage /> },
      { path: 'admin/users/:id', element: <UserDetailPage /> },
      { path: 'admin/roles', element: <RolesPage /> },
      { path: 'admin/roles/:code', element: <RoleDetailPage /> },
      { path: 'accounts', element: <ChartOfAccountsPage /> },
      { path: 'accounts/:id', element: <AccountDetailPage /> },
      { path: 'coa-import', element: <CoaImportPage /> },
      { path: 'periods', element: <AccountingPeriodsPage /> },
      { path: 'periods/:id', element: <PeriodDetailPage /> },
      { path: 'journals', element: <JournalsPage /> },
      { path: 'journals/new', element: <JournalEditorPage /> },
      { path: 'journals/:id', element: <JournalDetailPage /> },
      { path: 'journals/:id/edit', element: <JournalEditorPage /> },
      { path: 'suppliers', element: <SuppliersPage /> },
      { path: 'suppliers/:id', element: <SupplierDetailPage /> },
      { path: 'supplier-invoices', element: <SupplierInvoicesPage /> },
      { path: 'supplier-invoices/:id', element: <SupplierInvoiceDetailPage /> },
      { path: 'supplier-credit-notes', element: <CreditNotesPage /> },
      { path: 'supplier-credit-notes/new', element: <NewCreditNotePage /> },
      { path: 'supplier-credit-notes/:id', element: <CreditNoteDetailPage /> },
      { path: 'bank-cash', element: <BankCashPage /> },
      { path: 'bank-cash/movements/:id', element: <MovementDetailPage /> },
      { path: 'payments', element: <PaymentsPage /> },
      { path: 'payments/:id', element: <PaymentDetailPage /> },
      { path: 'expense-claims', element: <ExpenseClaimsPage /> },
      { path: 'expense-claims/new', element: <ExpenseClaimEditorPage /> },
      { path: 'expense-claims/:id', element: <ExpenseClaimDetailPage /> },
      { path: 'expense-claims/:id/edit', element: <ExpenseClaimEditorPage /> },
      { path: 'expense-categories', element: <ExpenseCategoriesPage /> },
      { path: 'customer-invoices', element: <CustomerInvoicesPage /> },
      { path: 'customer-invoices/new', element: <CustomerInvoiceEditorPage /> },
      { path: 'customer-invoices/:id', element: <CustomerInvoiceDetailPage /> },
      { path: 'customer-invoices/:id/edit', element: <CustomerInvoiceEditorPage /> },
      { path: 'customer-invoices/:id/print', element: <TaxInvoicePrintPage /> },
      { path: 'customer-credit-notes', element: <CustomerCreditNotesPage /> },
      { path: 'customer-credit-notes/new', element: <NewCustomerCreditNotePage /> },
      { path: 'customer-credit-notes/:id', element: <CustomerCreditNoteDetailPage /> },
      { path: 'customer-receipts', element: <CustomerReceiptsPage /> },
      { path: 'customer-receipts/new', element: <NewCustomerReceiptPage /> },
      { path: 'customer-receipts/:id', element: <CustomerReceiptDetailPage /> },
      { path: 'admin/configuration', element: <ConfigurationPage /> },
      { path: 'admin/lists', element: <ListValuesPage /> },
      { path: 'admin/number-sequences', element: <NumberSequencesPage /> },
      { path: 'admin/sensitive-fields', element: <SensitiveFieldsPage /> },
      { path: 'admin/sod-rules', element: <SodRulesPage /> },
      { path: 'admin/audit', element: <AuditTrailPage /> },
      { path: 'admin/workflows', element: <WorkflowRoutesPage /> },
      { path: 'admin/approval-chart', element: <ApprovalChartPage /> },
      { path: 'approvals', element: <ApprovalsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'chat/:conversationId', element: <ChatPage /> },
      { path: 'admin/notification-rules', element: <NotificationRulesPage /> },
      { path: 'admin/chat-groups', element: <ChatGroupsPage /> },
      { path: 'admin/chat-groups/:groupId', element: <ChatGroupDetailPage /> },
      { path: 'admin/api-description', element: <ApiDescriptionPage /> },
      { path: 'admin/document-types', element: <DocumentTypesPage /> },
      { path: 'documents/expiring', element: <ExpiringDocumentsPage /> },
      { path: 'assets', element: <AssetsPage /> },
      { path: 'assets/my', element: <MyAssetsPage /> },
      { path: 'assets/classes', element: <AssetClassesPage /> },
      { path: 'assets/:id', element: <AssetDetailPage /> },
      { path: 'organization/products-services', element: <ProductsServicesPage /> },
      { path: 'organization/products-services/:id', element: <ProductServiceDetailPage /> },
      { path: 'crm/clients', element: <ClientsPage /> },
      { path: 'crm/clients/:id', element: <ClientDetailPage /> },
      { path: 'crm/contacts/:id', element: <ContactDetailPage /> },
      { path: 'crm/activities', element: <ActivitiesPage /> },
      { path: 'crm/activities/:id', element: <ActivityDetailPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'projects/:id', element: <ProjectDetailPage /> },
      { path: 'projects/:id/work', element: <ProjectWorkPage /> },
      { path: 'work', element: <WorkProjectsPage /> },
      { path: 'my-tasks', element: <MyTasksPage /> },
      { path: 'hr/leave', element: <MyLeavePage /> },
      { path: 'hr/leave/requests', element: <LeaveRequestsPage /> },
      { path: 'hr/leave/requests/:id', element: <LeaveRequestDetailPage /> },
      { path: 'hr/leave/cancellations/:id', element: <LeaveCancellationDetailPage /> },
      { path: 'hr/leave-types', element: <LeaveTypesPage /> },
      { path: 'hr/public-holidays', element: <PublicHolidaysPage /> },
      { path: 'hr/announcements', element: <AnnouncementsPage /> },
      { path: 'hr/announcements/:id', element: <AnnouncementDetailPage /> },
      { path: 'hr/onboarding', element: <OnboardingPage /> },
      { path: 'hr/onboarding-templates', element: <OnboardingTemplatesPage /> },
      { path: 'hr/my-onboarding-tasks', element: <MyOnboardingTasksPage /> },
      { path: 'hr/my-requests', element: <MyRequestsPage /> },
      { path: 'hr/requests', element: <EmployeeRequestsPage /> },
      { path: 'hr/requests/:id', element: <EmployeeRequestDetailPage /> },
      { path: 'migration/batches', element: <BatchesPage /> },
      { path: 'migration/batches/:id', element: <BatchDetailPage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'marketing/leads', element: <LeadsPage /> },
      { path: 'marketing/leads/:id', element: <LeadDetailPage /> },
      { path: 'marketing/campaigns', element: <CampaignsPage /> },
      { path: 'marketing/campaigns/:id', element: <CampaignDetailPage /> },
      { path: 'hr/happiness', element: <HappinessPage /> },
      { path: 'quality/issues', element: <QualityIssuesPage /> },
      { path: 'quality/issues/:id', element: <QualityIssueDetailPage /> },
      { path: 'procurement/purchase-requests', element: <PurchaseRequestsPage /> },
      { path: 'procurement/purchase-requests/new', element: <NewPurchaseRequestPage /> },
      { path: 'procurement/purchase-requests/:id', element: <PurchaseRequestDetailPage /> },
      { path: 'procurement/purchase-requests/:id/edit', element: <EditPurchaseRequestPage /> },
      { path: 'procurement/my-purchase-requests', element: <MyPurchaseRequestsPage /> },
      { path: 'procurement/purchase-orders', element: <PurchaseOrdersPage /> },
      { path: 'procurement/purchase-orders/new', element: <NewPurchaseOrderPage /> },
      { path: 'procurement/purchase-orders/:id', element: <PurchaseOrderDetailPage /> },
      { path: 'procurement/purchase-orders/:id/edit', element: <EditPurchaseOrderPage /> },
      { path: 'procurement/receipts', element: <GoodsReceiptsPage /> },
      { path: 'procurement/receipts/:id', element: <GoodsReceiptDetailPage /> },
      { path: 'dashboards/:code', element: <ReportingDashboardPage /> },
      { path: 'reports', element: <FinancialReportsPage /> },
      { path: 'reports/trial-balance', element: <TrialBalancePage /> },
      { path: 'reports/account-ledger', element: <AccountLedgerPage /> },
      { path: 'reports/profit-and-loss', element: <ProfitAndLossPage /> },
      { path: 'reports/balance-sheet', element: <BalanceSheetPage /> },
      { path: 'reports/posting-rejections', element: <PostingRejectionsPage /> },
      { path: 'reports/customer-outstanding', element: <CustomerOutstandingPage /> },
      { path: 'reports/vat-transactions', element: <VatTransactionsPage /> },
      { path: 'reports/expenses', element: <AnalysisReportPage kind="expense" /> },
      { path: 'reports/revenue', element: <AnalysisReportPage kind="revenue" /> },
      { path: 'reports/vat-exceptions', element: <VatExceptionsPage /> },
      { path: 'reports/vat-reconciliation', element: <VatReconciliationPage /> },
      { path: 'reports/taxable-supplies', element: <TaxableSuppliesPage /> },
      { path: 'reports/supplier-outstanding', element: <SupplierOutstandingPage /> },
      { path: 'reports/reconciliation-status', element: <ReconciliationStatusPage /> },
      {
        path: 'reports/opening-balance-reconciliation',
        element: <OpeningBalanceReconciliationPage />,
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export function createQueryClient(): QueryClient {
  // Mutations are never retried automatically (SDD §22.1). Reads are not retried either: the API
  // answers a refusal deliberately, and repeating it only delays showing the person what happened.
  return new QueryClient({
    defaultOptions: { mutations: { retry: false }, queries: { retry: false } },
  });
}

export function App({ initialPath }: { initialPath?: string }) {
  // One router and one query cache for as long as the app is mounted, not one per render.
  const [router] = useState(() =>
    initialPath === undefined
      ? createBrowserRouter(routes)
      : createMemoryRouter(routes, { initialEntries: [initialPath] }),
  );
  const [queryClient] = useState(createQueryClient);
  return (
    <CacheProvider value={appEmotionCache()}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider router={router} />
        </ThemeProvider>
      </QueryClientProvider>
    </CacheProvider>
  );
}
